/**
 * Supported file formats in the web app
 */
export enum Format {
  CSV = 'csv',
  PARQUET = 'parquet',
  TSV = 'tsv',
  XLSX = 'xlsx',
  JSON = 'json'
}

/**
 * Readable formats (all formats except XLSX)
 * Used for sitemap and page generation
 */
export const READABLE_FORMATS = [
  Format.CSV,
  Format.PARQUET,
  Format.TSV,
  Format.JSON
];

/**
 * Writable formats (all formats including XLSX)
 * Used for conversion targets
 */
export const WRITABLE_FORMATS = Object.values(Format);

/**
 * Maps formats to their MIME types
 */
export const FORMAT_MIME_TYPES: Record<Format, string> = {
  [Format.CSV]: 'text/csv',
  [Format.TSV]: 'text/tab-separated-values',
  [Format.JSON]: 'application/json',
  [Format.PARQUET]: 'application/octet-stream,.parquet',
  [Format.XLSX]: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

/**
 * Gets the file extension for a format (including the dot)
 */
export function getFileExtension(format: Format): string {
  return `.${format}`
}

/**
 * Gets the properly formatted display name for a format
 * This is the centralized function to use for displaying format names
 * throughout the application to ensure consistency
 */
export function getFormatDisplayName(format: Format): string {
  switch (format) {
    case Format.CSV:
      return 'CSV';
    case Format.TSV:
      return 'TSV';
    case Format.JSON:
      return 'JSON';
    case Format.PARQUET:
      return 'Parquet';
    case Format.XLSX:
      return 'Excel';
    default:
      return format;
  }
}

/**
 * Checks if a format is readable (all formats except XLSX)
 */
export function isReadableFormat(format: Format): boolean {
  return READABLE_FORMATS.includes(format);
}

/**
 * Checks if a format is writable (all formats including XLSX)
 */
export function isWritableFormat(format: Format): boolean {
  return WRITABLE_FORMATS.includes(format);
}

/**
 * Get format from a file path or filename
 * This is a centralized function to determine format from path
 * throughout the application to ensure consistency
 * 
 * @param path - The file path or name to get the format from
 * @returns The detected Format enum or Format.CSV as default
 */
export function getFormatFromPath(path: string): Format {
  if (!path) return Format.CSV;
  
  const extension = path.split('.').pop()?.toLowerCase();
  if (!extension) return Format.CSV;
  
  // Direct match with format enum
  if (Object.values(Format).includes(extension as Format)) {
    return extension as Format;
  }
  
  // Handle special cases and alternative extensions
  switch (extension) {
    // CSV and variants
    case 'csv':
    case 'txt':
      return Format.CSV;
    
    // TSV and variants
    case 'tsv':
    case 'tab':
      return Format.TSV;
    
    // JSON and variants
    case 'json':
    case 'jsonl':
    case 'ndjson':
      return Format.JSON;
    
    // Parquet and variants
    case 'parquet':
    case 'pq':
      return Format.PARQUET;
    
    // Excel and variants
    case 'xlsx':
    case 'xls':
      return Format.XLSX;
    
    // Default to CSV for unknown extensions
    default:
      return Format.CSV;
  }
} 